<template>
    <Row class="ActivityHistoryTableRow">
        <Cell class="ActivityHistoryTableRow__date">
            {{ formattedDate }}
        </Cell>
        <Cell class="ActivityHistoryTableRow__event">
            {{ eventLabel }}
        </Cell>
        <Cell class="ActivityHistoryTableRow__details">
            <span class="ActivityHistoryTableRow__detailsContent">
                <EidIcon
                    v-if="isCurrentEvaluation"
                    v-tooltip="'Current document used for compliance.'"
                    class="ActivityHistoryTableRow__currentEvalIcon"
                    alt="current evaluation"
                    :icon-src="faStar"
                />
                <EidIcon
                    v-if="requiresAttention"
                    class="ActivityHistoryTableRow__detailsWarning"
                    :icon-src="faTriangleExclamation"
                />
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="detailsHtml" />
            </span>
        </Cell>
        <Cell class="ActivityHistoryTableRow__source">
            {{ entry.causedBy }}
        </Cell>
        <Cell v-if="showTags" class="ActivityHistoryTableRow__tags">
            <div class="ActivityHistoryTableRow__tagsContainer">
                <LabelBadge
                    v-for="tag in allTags"
                    :key="`${tag.color}-${tag.value}`"
                    class="ActivityHistoryTableRow__tag"
                    :color="tag.color"
                    size="sm"
                >
                    {{ tag.label }}
                </LabelBadge>
            </div>
            <span v-if="allTags.length === 0">—</span>
        </Cell>
        <Cell v-if="showViewColumn" class="ActivityHistoryTableRow__view">
            <DocumentViewButton
                v-if="hasViewAction"
                @click="$emit('view', entry)"
            />
            <span v-else>—</span>
        </Cell>
    </Row>
</template>

<style lang="scss">
    .ActivityHistoryTableRow {
        &__date {
            white-space: nowrap;
            width: 120px;
        }

        &__event {
            width: 180px;
        }

        &__details {
            min-width: 300px;
        }

        &__detailsContent {
            display: inline-flex;
            align-items: center;
            gap: 4px;
        }

        &__currentEvalIcon {
            font-size: 0.8em;
            color: var(--eidTrustGreen);
            flex-shrink: 0;
        }

        &__detailsWarning {
            font-size: 0.8em;
            font-weight: 500;
            color: var(--eidDangerRed);
        }

        &__dangerText {
            color: var(--eidDangerRed);
            font-weight: 500;
        }

        &__tags {
            width: 220px;
        }

        &__tagsContainer {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
        }

        &__source {
            width: 160px;
            overflow-wrap: break-word;
        }

        &__view {
            width: 80px;
            text-align: right;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
    import { faStar } from '@fortawesome/pro-solid-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import LabelBadge from '@evidentid/dashboard-commons/components/LabelBadge/LabelBadge.vue';
    import { Row, Cell } from '@evidentid/dashboard-commons/components/Table';
    import {
        ActivityDeclineReason,
        ActivityHistoryEntry,
        ActivityHistorySystemTag,
        ActivityHistorySystemTags,
        ActivityHistoryTagsMap,
        ActivityType,
        ClientTag,
        ComplianceStatusChangedDetails,
        EntityPropertyChangedDetails,
        EvalOverrideDetails,
        EvaluationCompletedDetails,
        ManagedServicesActivityDetails,
        NotificationDetails,
        ResourceType,
        RiskProfileDetails,
        EntityActionResolvedDetails,
        EntityActionRevertedDetails,
        DocumentDataReviewDetails,
        VIEWABLE_RESOURCE_TYPES,
    } from '@evidentid/tprm-portal-lib/models/activity-history';
    import {
        TprmRequirementCategory,
        TprmRequirementDefinition,
    } from '@evidentid/tprm-portal-lib/models/common';
    import { TprmRequirementInstanceModel } from '@evidentid/tprm-portal-lib/models/common/TprmRequirementInstanceModel.model';
    import { ExceptionLevel, TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { EnumCategories } from '@/modules/dashboard/models/EnumCategories.model';
    import DocumentViewButton from '@/modules/entity-details/components/DocumentViewButton/DocumentViewButton.vue';
    import { escapeHtml } from '@/modules/entity-events/utils/escapeHtml';
    import { formatComplianceStatusChangedSummary } from '@/modules/entity-events/utils/formatComplianceStatusChangedSummary';
    import { formatDocumentDataReviewSummary } from '@/modules/entity-events/utils/formatDocumentDataReviewSummary';
    import { formatEntityActionResolvedSummary } from '@/modules/entity-events/utils/formatEntityActionResolvedSummary';
    import { formatEntityActionRevertedSummary } from '@/modules/entity-events/utils/formatEntityActionRevertedSummary';
    import { formatEntityPropertyChangedSummary } from '@/modules/entity-events/utils/formatEntityPropertyChangedSummary';
    import { getNotificationPrefix } from '@/modules/entity-events/utils/getNotificationPrefix';
    import { humanizeCriterionField } from '@/modules/entity-events/utils/humanizeCriterionField';
    import { formatGlobalEventSummary } from '@/modules/global-events/utils/formatGlobalEventSummary';
    import { formatDateTime } from '@/utils/formatDateTime';
    import { getRequirementInstanceLabel } from '@/utils/get-requirement-instance-label/getRequirementInstanceLabel';

    export default defineComponent({
        name: 'ActivityHistoryTableRow',
        components: { EidIcon, LabelBadge, DocumentViewButton, Row, Cell },
        props: {
            entry: {
                type: Object as PropType<ActivityHistoryEntry>,
                required: true,
            },
            enumLabels: {
                type: Object as PropType<CategorizedEnumLabels>,
                default: (): CategorizedEnumLabels => ({}) as CategorizedEnumLabels,
            },
            requirementDefinitions: {
                type: Object as PropType<Record<TprmRequirementType, TprmRequirementDefinition>>,
                default: (): Record<TprmRequirementType, TprmRequirementDefinition> => (
                    {} as Record<TprmRequirementType, TprmRequirementDefinition>
                ),
            },
            requirementInstanceModels: {
                type: Object as PropType<Record<TprmRequirementCategory, TprmRequirementInstanceModel[]>>,
                default: (): Record<TprmRequirementCategory, TprmRequirementInstanceModel[]> => (
                    {} as Record<TprmRequirementCategory, TprmRequirementInstanceModel[]>
                ),
            },
            clientTagsMap: {
                type: Object as PropType<ActivityHistoryTagsMap | null>,
                default: null,
            },
            systemTagsMap: {
                type: Object as PropType<ActivityHistorySystemTags | null>,
                default: null,
            },
            activeVerificationIds: {
                type: Array as PropType<string[]>,
                default: (): string[] => [] as string[],
            },
            criterionTitles: {
                type: Object as PropType<Record<string, string>>,
                default: (): Record<string, string> => ({}) as Record<string, string>,
            },
            showTags: {
                type: Boolean,
                default: true,
            },
            showViewColumn: {
                type: Boolean,
                default: true,
            },
        },
        emits: [ 'view' ],
        data() {
            return { faTriangleExclamation, faStar };
        },
        computed: {
            isCurrentEvaluation(): boolean {
                if (this.entry.activityType !== ActivityType.EVALUATION_COMPLETED) {
                    return false;
                }
                const evalDetails = this.entry.details as EvaluationCompletedDetails | null;
                return Boolean(
                    evalDetails?.verificationId &&
                        (this.activeVerificationIds as string[]).includes(evalDetails.verificationId),
                ) && !this.requiresAttention;
            },
            requiresAttention(): boolean {
                if (this.entry.activityType !== ActivityType.EVALUATION_COMPLETED) {
                    return false;
                }
                const evalDetails = this.entry.details as EvaluationCompletedDetails | null;
                return Boolean(evalDetails?.declineReason) || evalDetails?.hasExtractedData === false;
            },
            formattedDate(): string {
                return formatDateTime(this.entry.createdAt);
            },
            eventLabel(): string {
                const labels = this.enumLabels[EnumCategories.historyEntryType] || {};
                return labels[this.entry.activityType]?.label || this.entry.activityType;
            },
            coverageTypeLabel(): string {
                if (!this.entry.details || !('coverageType' in this.entry.details)) {
                    return '';
                }
                const { coverageType } = this.entry.details;
                if (!coverageType) {
                    return '';
                }
                const labels = this.enumLabels[EnumCategories.coverageTypes] || {};
                return labels[coverageType]?.label || coverageType;
            },
            instanceLabel(): string | null {
                if (
                    !this.entry.details ||
                    !('instanceId' in this.entry.details) ||
                    !this.entry.details.instanceId
                ) {
                    return null;
                }
                return getRequirementInstanceLabel(
                    this.entry.details.coverageType as TprmRequirementType,
                    this.requirementDefinitions,
                    this.entry.details.instanceId,
                    this.requirementInstanceModels,
                );
            },
            requirementLabel(): string {
                return this.instanceLabel
                    ? `${this.coverageTypeLabel} - ${this.instanceLabel}`
                    : this.coverageTypeLabel;
            },
            criterionLabel(): string {
                const details = this.entry.details as EvalOverrideDetails | null;
                const field = details?.criterionField;
                if (!field) {
                    return '';
                }
                return this.criterionTitles[field] || humanizeCriterionField(field);
            },
            exceptionScopeHtml(): string {
                // Bolds the scope subject the same way Request Created/Terminated bold the requirement label:
                // the criterion title for criterion-level events, otherwise the coverage label.
                const details = this.entry.details as EvalOverrideDetails | null;
                const bold = (value: string): string => `<strong>${value}</strong>`;
                switch (details?.level) {
                    case ExceptionLevel.coverageCriterion:
                        return this.criterionLabel && this.coverageTypeLabel
                            ? `${bold(this.criterionLabel)} in ${this.coverageTypeLabel}`
                            : (this.coverageTypeLabel ? bold(this.coverageTypeLabel) : 'the entity');
                    case ExceptionLevel.requirementInstance:
                        return this.coverageTypeLabel ? bold(this.coverageTypeLabel) : 'the entity';
                    case ExceptionLevel.evaluatedEntity:
                        return 'the evaluated entity';
                    case ExceptionLevel.insured:
                        return 'the entity';
                    default:
                        // legacy rows (pre-PRODUCT-26098) carry only a backend-composed scope sentence
                        return details?.scope || 'the entity';
                }
            },
            detailsHtml(): string {
                const { activityType, details } = this.entry;
                const globalSummary = formatGlobalEventSummary(activityType, details);
                if (globalSummary !== null) {
                    return globalSummary;
                }
                const req = `<strong>${this.requirementLabel}</strong>`;
                switch (activityType) {
                    // Names no requirement: one entry covers the whole request however many were
                    // reopened, and the REQUEST_CREATED rows it sits above name them one each.
                    case ActivityType.NEW_LINK_CREATED:
                        return 'A new request link was created for this entity';
                    case ActivityType.REQUEST_CREATED:
                        return `Submission Request for ${req} was initiated`;
                    case ActivityType.RESPONSE_RECEIVED:
                        return `Entity responded to request for ${req}`;
                    case ActivityType.REQUEST_TIMEOUT:
                        return `Request timed out for ${req}`;
                    case ActivityType.REQUEST_TERMINATED:
                        return `Request terminated for ${req}`;
                    case ActivityType.EVALUATION_COMPLETED: {
                        const evalDetails = details as EvaluationCompletedDetails | null;
                        const declineHtml = this.getDeclineReasonHtml(evalDetails?.declineReason, req);
                        if (declineHtml !== null) {
                            return declineHtml;
                        }
                        if (evalDetails?.hasExtractedData === false) {
                            return `${req} ${this.redTextHtml('was not provided')} in the submission`;
                        }
                        return `Evaluation completed for ${req}`;
                    }
                    case ActivityType.FORCED_SUBMISSION:
                        return 'Processing was forced';
                    case ActivityType.NOTE_ADDED:
                        return 'Note added to the entity';
                    case ActivityType.DOCUMENT_UPLOADED:
                        return 'Document added to the entity';
                    case ActivityType.EMAIL_SENT:
                    case ActivityType.EMAIL_ERROR: {
                        const notificationDetails = details as NotificationDetails | null;
                        if (!notificationDetails) {
                            return activityType;
                        }
                        const prefix = escapeHtml(getNotificationPrefix(notificationDetails));
                        const recipient = escapeHtml(notificationDetails.recipientEmail);
                        const cc = (notificationDetails.cc ?? []).map((email) => escapeHtml(email));
                        const ccSuffix = cc.length ? ` (cc: ${cc.join(', ')})` : '';
                        return activityType === ActivityType.EMAIL_SENT
                            ? `${prefix} email sent to ${recipient}${ccSuffix}`
                            : `${prefix} email to ${recipient} ${this.redTextHtml('failed')}`;
                    }
                    case ActivityType.EVAL_OVERRIDE_GRANTED:
                        return `An exception was granted for ${this.exceptionScopeHtml}`;
                    case ActivityType.EVAL_OVERRIDE_REMOVED:
                        return `An exception was removed for ${this.exceptionScopeHtml}`;
                    case ActivityType.INSURED_PAUSED:
                        return 'Verification requests were paused for this entity';
                    case ActivityType.INSURED_UNPAUSED:
                        return 'Verification requests were resumed for this entity';
                    case ActivityType.ENTITY_DEACTIVATED:
                        return 'Entity was deactivated';
                    case ActivityType.ENTITY_ACTIVATED:
                        return 'Entity was activated';
                    case ActivityType.ENTITY_PROPERTY_CHANGED: {
                        const propertyDetails = details as EntityPropertyChangedDetails | null;
                        return formatEntityPropertyChangedSummary(propertyDetails?.changedPropertiesDisplayNames);
                    }
                    case ActivityType.COMPLIANCE_STATUS_CHANGED: {
                        const statusDetails = details as ComplianceStatusChangedDetails | null;
                        return formatComplianceStatusChangedSummary(statusDetails, this.enumLabels);
                    }
                    case ActivityType.RISK_PROFILE_ADDED:
                    case ActivityType.RISK_PROFILE_REMOVED: {
                        const riskProfileDetails = details as RiskProfileDetails | null;
                        if (!riskProfileDetails?.riskProfileName) {
                            return activityType;
                        }
                        // The name is customer-supplied and this string is rendered with v-html.
                        const profile = `<strong>${escapeHtml(riskProfileDetails.riskProfileName)}</strong>`;
                        return activityType === ActivityType.RISK_PROFILE_ADDED
                            ? `Entity added to risk profile ${profile}`
                            : `Entity removed from risk profile ${profile}`;
                    }
                    case ActivityType.ENTITY_ACTION_RESOLVED: {
                        // Every part of this sentence is a backend enum value or a label from a map in
                        // this repo - nothing customer-supplied reaches it - so it needs no escaping,
                        // unlike the risk profile name above.
                        const actionDetails = details as EntityActionResolvedDetails | null;
                        return formatEntityActionResolvedSummary(actionDetails);
                    }
                    case ActivityType.ENTITY_ACTION_REVERTED: {
                        // The reviewer's reason is a note behind the view action, not part of this
                        // sentence, so nothing customer-supplied reaches the string and it needs no
                        // escaping.
                        const revertDetails = details as EntityActionRevertedDetails | null;
                        return formatEntityActionRevertedSummary(revertDetails);
                    }
                    case ActivityType.DOCUMENT_DATA_REVIEW_COMPLETED: {
                        // Every part of this sentence comes from the requirement definitions or a literal in
                        // this repo - nothing customer-supplied - so it needs no escaping.
                        const reviewDetails = details as DocumentDataReviewDetails | null;
                        return formatDocumentDataReviewSummary(reviewDetails, this.requirementDefinitions);
                    }
                    case ActivityType.MEMBER_OUTREACH:
                    case ActivityType.PRODUCER_OUTREACH: {
                        const outreachDetails = details as ManagedServicesActivityDetails | null;
                        return outreachDetails?.description
                            ? escapeHtml(outreachDetails.description)
                            : activityType;
                    }
                    default:
                        return activityType;
                }
            },
            allTags(): { value: string, label: string, color: 'info' | 'success' }[] {
                const resourceSystemTags: ActivityHistorySystemTag[] =
                    this.systemTagsMap?.[this.entry.resourceType] || [];
                const systemTagLabelMap = new Map(
                    resourceSystemTags.map((t: ActivityHistorySystemTag) => [ t.key, t.label ]),
                );
                const systemTags = this.entry.systemTags.map((tag: string) => ({
                    value: tag,
                    label: systemTagLabelMap.get(tag) || tag,
                    color: 'info' as const,
                }));
                const resourceClientTags: ClientTag[] =
                    this.clientTagsMap?.[this.entry.resourceType] || [];
                const clientTagLabelMap = new Map(resourceClientTags.map((t: ClientTag) => [ t.key, t.label ]));
                const clientTags = this.entry.clientTags.map((tag: string) => ({
                    value: tag,
                    label: clientTagLabelMap.get(tag) || tag,
                    color: 'success' as const,
                }));
                return [ ...systemTags, ...clientTags ];
            },
            hasViewAction(): boolean {
                if (this.entry.activityType === ActivityType.EVAL_OVERRIDE_GRANTED) {
                    return true;
                }
                if (this.entry.resourceType === ResourceType.EVAL_OVERRIDE) {
                    return (this.entry.details as EvalOverrideDetails | null)?.note != null;
                }
                if (this.entry.activityType === ActivityType.ENTITY_ACTION_REVERTED) {
                    return (this.entry.details as EntityActionRevertedDetails | null)?.revertReason != null;
                }
                if (this.entry.activityType === ActivityType.ENTITY_PROPERTY_CHANGED) {
                    return Boolean((this.entry.details as EntityPropertyChangedDetails | null)
                        ?.changedPropertiesDisplayNames?.length);
                }
                return VIEWABLE_RESOURCE_TYPES.includes(this.entry.resourceType as ResourceType) ||
                    this.entry.activityType === ActivityType.RESPONSE_RECEIVED ||
                    this.entry.activityType === ActivityType.EMAIL_SENT ||
                    this.entry.activityType === ActivityType.EMAIL_ERROR;
            },
        },
        methods: {
            redTextHtml(text: string): string {
                return `<span class="ActivityHistoryTableRow__dangerText">${text}</span>`;
            },
            getDeclineReasonHtml(reason: ActivityDeclineReason | undefined, req: string): string | null {
                if (reason === ActivityDeclineReason.DECLINED) {
                    return `Entity ${this.redTextHtml('declined to provide')} ${req}`;
                }
                if (reason === ActivityDeclineReason.EXCEPTION_REQUESTED) {
                    return `Entity ${this.redTextHtml('requested an exception')} for ${req}`;
                }
                if (reason === ActivityDeclineReason.MISSING) {
                    return `Entity ${this.redTextHtml('doesn\'t have this')} ${req}`;
                }
                if (reason === ActivityDeclineReason.NOT_RENEWED) {
                    return `${req} ${this.redTextHtml('was not renewed')}`;
                }
                return null;
            },
        },
    });
</script>
